import React, { useContext, useEffect, useMemo, useState } from 'react';
import { DataContext } from '../components/DataContext';
import TableViewer from './TableViewer';
import '../styles/Managers.css';

const DAY = 24 * 60 * 60 * 1000;
const extractEndDate = (value) => {
    const match = value?.replace('Period: ', '').match(/\[(\d+), (\d+), (\d+)\] to \[(\d+), (\d+), (\d+)\]/);
    return match ? new Date(Number(match[6]), Number(match[4]) - 1, Number(match[5])) : new Date(0);
};

const columns = {
    employee: { name: 'Employee', pos: 1 },
    sales_per_hour: { name: 'Sales / Hr', type: 'float', pos: 2, just: 'right' },
    sales_bonus: { name: 'Sales Bonus', type: 'currency', pos: 3, just: 'right' },
    gp_per_hour: { name: 'GP / Hr', type: 'currency', pos: 4, just: 'right' },
    total_gp: { name: 'Total GP', type: 'currency', pos: 5, just: 'right' },
    est_commission: { name: 'Est. Commission', type: 'currency', pos: 6, just: 'right' },
    sales: { name: 'Sales', type: 'float', pos: 7, just: 'right' },
    repairs: { name: 'Repairs', type: 'float', pos: 8, just: 'right' },
};

const Managers = () => {
    const { employeeKpis = [], loadingEmployee } = useContext(DataContext);
    const [visibleEmployees, setVisibleEmployees] = useState([]);
    const [selectedRange, setSelectedRange] = useState(30);
    const [view, setView] = useState('cards');
    const [showModal, setShowModal] = useState(false);
    const [initialized, setInitialized] = useState(false);

    const allEmployees = useMemo(() => [...new Set(employeeKpis.map((item) => item.employee).filter(Boolean))].sort(), [employeeKpis]);
    const recentEmployees = useMemo(() => {
        const now = Date.now();
        return allEmployees.filter((employee) => employeeKpis.some((item) =>
            item.employee === employee && item.week_summary && now - extractEndDate(item.week_summary) <= 30 * DAY && now >= extractEndDate(item.week_summary)
        ));
    }, [allEmployees, employeeKpis]);

    useEffect(() => {
        if (!initialized && allEmployees.length) {
            setVisibleEmployees(recentEmployees);
            setInitialized(true);
        }
    }, [allEmployees, initialized, recentEmployees]);

    const rows = useMemo(() => {
        const now = Date.now();
        return visibleEmployees.map((employee) => {
            const records = employeeKpis.filter((item) => {
                const end = extractEndDate(item.week_summary).getTime();
                return item.employee === employee && end > 0 && now >= end && now - end <= selectedRange * DAY;
            });
            if (!records.length) return { employee };
            const total = (field) => records.reduce((sum, item) => sum + (Number(item[field]) || 0), 0);
            const hours = total('hours');
            const sales = total('sales');
            const totalGp = total('total_gp');
            return {
                employee,
                sales_per_hour: hours ? sales / hours : 0,
                sales_bonus: total('sales_bonus') / records.length,
                gp_per_hour: hours ? totalGp / hours : 0,
                total_gp: totalGp / records.length,
                est_commission: total('est_commission') / records.length,
                sales: sales / records.length,
                repairs: total('repairs') / records.length,
            };
        });
    }, [employeeKpis, selectedRange, visibleEmployees]);

    const toggleEmployee = (employee) => setVisibleEmployees((current) =>
        current.includes(employee) ? current.filter((name) => name !== employee) : [...current, employee]
    );

    return (
        <div className="managers-container">
            <div className="manager-heading">
                <div><p className="eyebrow">Team performance</p><h1>Manager Dashboard</h1></div>
                <div className="view-switch" aria-label="Manager display style">
                    <button type="button" className={view === 'cards' ? 'active' : ''} onClick={() => setView('cards')}>Cards</button>
                    <button type="button" className={view === 'table' ? 'active' : ''} onClick={() => setView('table')}>Table</button>
                </div>
            </div>

            {loadingEmployee ? <p>Loading employee data…</p> : (
                <>
                    <div className="manager-controls">
                        <div className="range-selector" aria-label="Average period">
                            {[30, 60, 90].map((range) => (
                                <button type="button" key={range} className={range === selectedRange ? 'active' : ''} onClick={() => setSelectedRange(range)}>{range} Days</button>
                            ))}
                        </div>
                        <button type="button" className="employee-selector-button" onClick={() => setShowModal(true)}>
                            Select employees <span>{visibleEmployees.length}</span>
                        </button>
                    </div>

                    {showModal && (
                        <div className="modal-overlay" onMouseDown={(event) => event.target === event.currentTarget && setShowModal(false)}>
                            <div className="modal-content" role="dialog" aria-modal="true" aria-labelledby="employee-modal-title">
                                <div className="popup-header"><div><h3 id="employee-modal-title">Select employees</h3><small>Selections apply to both views.</small></div><button type="button" className="close-button" onClick={() => setShowModal(false)} aria-label="Close employee selector">×</button></div>
                                <div className="modal-columns-grid">
                                    {allEmployees.map((employee) => {
                                        const selected = visibleEmployees.includes(employee);
                                        return <button type="button" key={employee} className={`modal-grid-item ${selected ? 'selected' : ''}`} aria-pressed={selected} onClick={() => toggleEmployee(employee)}>{employee}</button>;
                                    })}
                                </div>
                                <div className="modal-buttons"><button type="button" onClick={() => setVisibleEmployees(recentEmployees)}>Restore default</button><button type="button" onClick={() => setShowModal(false)}>Apply employees</button></div>
                            </div>
                        </div>
                    )}

                    {!visibleEmployees.length ? <div className="empty-state">Select at least one employee to compare performance.</div> : view === 'table' ? (
                        <TableViewer dataKey={rows} colFormatDict={columns} defaultSortBy="gp_per_hour" />
                    ) : (
                        <div className="manager-cards-grid">
                            {rows.map((row) => (
                                <article className="manager-card" key={row.employee}>
                                    <h2>{row.employee}</h2>
                                    <p className="manager-average-label">Average # of:</p>
                                    {row.gp_per_hour === undefined ? <p className="manager-no-data">No data found for the last {selectedRange} days.</p> : (
                                        <div className="manager-metrics">
                                            <p><strong>Sales / Hr</strong><span>{row.sales_per_hour.toFixed(2)}</span></p>
                                            <p><strong>Sales Bonus</strong><span>${row.sales_bonus.toFixed(2)}</span></p>
                                            <p><strong>GP / Hr</strong><span>${row.gp_per_hour.toFixed(2)}</span></p>
                                            <p><strong>Total GP</strong><span>${row.total_gp.toFixed(2)}</span></p>
                                            <p><strong>Est. Commission</strong><span>${row.est_commission.toFixed(2)}</span></p>
                                            <p><strong>Sales</strong><span>{row.sales.toFixed(2)}</span></p>
                                            <p><strong>Repairs</strong><span>{row.repairs.toFixed(2)}</span></p>
                                        </div>
                                    )}
                                </article>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default Managers;
